import { useState, useEffect, useRef } from "react";
import { CustomLoader } from "@admin/components/ui/custom-loader";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Building,
  Users,
  Heart,
  Shield,
  Home,
  FileText,
  CheckCircle,
  Clock,
  UserCog,
} from "lucide-react";
import { usePrefixedNavigate } from "../../hooks/usePrefixedNavigate";
import { Button } from "../../components/ui/button";
import { dashboardAPI, type DashboardData } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";

export default function SuperAdminDashboard() {
  const navigate = usePrefixedNavigate();
  const { userRole } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (userRole === "management") {
      navigate("/super-admin/institutions");
      return;
    }
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;

    const fetchDashboard = async () => {
      try {
        const result = await dashboardAPI.get();
        setData(result);
      } catch (error) {
        console.error("Error fetching dashboard:", error);
        setData({
          total_institute: 0,
          pending_approvals: 0,
          approved_institution: 0,
          rejected_institution: 0,
          total_student: 0,
          donors: 0,
          total_donations: 0,
          user_overview: { student: 0, donors: 0, managements: 0, trustees: 0 },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [userRole, navigate]);

  const u = data?.user_overview;
  const students = u?.student ?? data?.total_student ?? 0;
  const donorsCount = data?.donors ?? u?.donors ?? 0;
  const managements = u?.managements ?? 0;
  const trustees = u?.trustees ?? 0;

  const kpiCards = [
    {
      title: "Total Institutions",
      value: String(data?.total_institute ?? 0),
      icon: Building,
      color: "text-primary",
      path: "/super-admin/institutions",
    },
    {
      title: "Pending Approvals",
      value: String(data?.pending_approvals ?? 0),
      icon: Clock,
      color: "text-yellow-600",
      path: "/super-admin/institutions",
    },
    {
      title: "Approved Institutions",
      value: String(data?.approved_institution ?? 0),
      icon: CheckCircle,
      color: "text-green-600",
      path: "/super-admin/institutions",
    },
    {
      title: "Rejected Institutions",
      value: String(data?.rejected_institution ?? 0),
      icon: Home,
      color: "text-red-600",
      path: "/super-admin/institutions",
    },
    {
      title: "Total Students",
      value: String(students),
      icon: Users,
      color: "text-primary",
      path: "/super-admin/student-inquiries",
    },
    {
      title: "Donors",
      value: String(donorsCount),
      icon: Heart,
      color: "text-rose-600",
      path: "/super-admin/funding",
    },
    {
      title: "Managements",
      value: String(managements),
      icon: UserCog,
      color: "text-purple-600",
      path: "/super-admin/user-management",
    },
    {
      title: "Trustees",
      value: String(trustees ?? 0),
      icon: Shield,
      color: "text-amber-600",
      path: "/super-admin/user-management",
    },
    {
      title: "Total Donations",
      value:
        (data?.total_donations ?? 0) >= 1000
          ? `₹${((data?.total_donations ?? 0) / 1000).toFixed(1)}K`
          : `₹${(data?.total_donations ?? 0).toFixed(0)}`,
      icon: FileText,
      color: "text-green-600",
      path: "/super-admin/funding",
    },
  ];

  if (loading) {
    return <CustomLoader fullScreen />;
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="mb-2 text-3xl font-bold text-foreground">
          Super Admin Dashboard
        </h1>
        <p className="text-muted-foreground">
          Complete overview and control of the JBF Portal
        </p>
      </div>

      {/* Pending Approvals quick action */}
      {(data?.pending_approvals ?? 0) > 0 && (
        <Card className="border-yellow-500/50">
          <CardHeader className="flex flex-row items-center justify-between p-3">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Clock className="h-5 w-5 text-yellow-600" />
              Pending Approvals ({data?.pending_approvals ?? 0})
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/super-admin/institutions")}
              className="space-y-0"
            >
              Review
            </Button>
          </CardHeader>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {kpiCards.map(card => {
          const Icon = card.icon;
          return (
            <Card
              key={`kpi-${card.title}`}
              className="cursor-pointer border-primary/20 transition-all hover:-translate-y-1 hover:shadow-lg"
              onClick={() => navigate(card.path)}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {card.title}
                </CardTitle>
                <Icon className={`h-5 w-5 ${card.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-foreground">
                  {card.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Institution & User Overview */}
      {/* <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Institution Status Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-green-500" />
                  <span>Approved</span>
                </div>
                <span className="font-semibold">
                  {data?.approved_institution ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-yellow-500" />
                  <span>Pending</span>
                </div>
                <span className="font-semibold">
                  {data?.pending_approvals ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                  <span>Rejected</span>
                </div>
                <span className="font-semibold">
                  {data?.rejected_institution ?? 0}
                </span>
              </div>
              <div className="border-t pt-4">
                <Button
                  className="w-full"
                  onClick={() => navigate("/super-admin/institutions")}
                >
                  Manage Institutions
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span>Students</span>
                </div>
                <span className="font-semibold">{students}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Heart className="h-4 w-4 text-rose-600" />
                  <span>Donors</span>
                </div>
                <span className="font-semibold">{donorsCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCog className="h-4 w-4 text-purple-600" />
                  <span>Managements</span>
                </div>
                <span className="font-semibold">{managements}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-amber-600" />
                  <span>Trustees</span>
                </div>
                <span className="font-semibold">{trustees ?? 0}</span>
              </div>
              <div className="border-t pt-4">
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => navigate("/super-admin/user-management")}
                >
                  Manage Users
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* No Pending Approvals */}
      {(data?.pending_approvals ?? 0) === 0 && (
        <Card className="border-green-500/50 bg-green-50/50 dark:bg-green-950/20">
          <CardContent className="py-8 text-center">
            <CheckCircle className="mx-auto mb-4 h-12 w-12 text-green-600" />
            <h3 className="text-lg font-semibold text-green-800 dark:text-green-400">
              All Caught Up!
            </h3>
            <p className="text-green-600 dark:text-green-500">
              No pending approvals at the moment.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
